import { useCallback } from "react";

import { buildModelRevisionsArgs, selectSceneRevision } from "apis/procore/modelRevisions";
import { useLazyGetModelRevisionsQuery, useLazyGetModelsQuery } from "apis/procore/procoreApi";
import { useAppStore } from "app/redux-store-interactions";
import { useSceneId } from "hooks/useSceneId";
import { selectBackendProvider, selectProcoreContext, selectProjectName } from "slices/explorer";

/**
 * What the open scene is called offline: standalone, the scene config's title; Procore, its
 * model's name, shared by every version and slice of the model. `undefined` where none can be had,
 * and the SDK then keeps the name the record holds. Never rejects.
 *
 * Read from the store when called, so the callback keeps its identity: it is a dependency of the
 * effect that runs the offline actions.
 */
export function useGetOfflineSceneName(): () => Promise<string | undefined> {
    const store = useAppStore();
    const sceneId = useSceneId();
    const getProcoreModelTitle = useGetProcoreModelTitle();

    return useCallback(async () => {
        const state = store.getState();
        const name =
            selectBackendProvider(state) === "procore" ? await getProcoreModelTitle(sceneId) : selectProjectName(state);
        return name || undefined;
    }, [store, sceneId, getProcoreModelTitle]);
}

/** The open revision's model's name, from the session's cache when it has it; `undefined` when it can't be had. */
function useGetProcoreModelTitle(): (sceneId: string) => Promise<string | undefined> {
    const store = useAppStore();
    const [getModelRevisions] = useLazyGetModelRevisionsQuery();
    const [getModels] = useLazyGetModelsQuery();

    return useCallback(
        async (sceneId: string) => {
            const procoreContext = selectProcoreContext(store.getState());
            const revisionsArgs = buildModelRevisionsArgs(procoreContext, sceneId);
            if (!procoreContext || !revisionsArgs) {
                return undefined;
            }

            try {
                const [modelRevisionsData, modelsData] = await Promise.all([
                    getModelRevisions(revisionsArgs, true).unwrap(),
                    getModels(
                        { companyId: procoreContext.companyId, projectId: procoreContext.projectId },
                        true,
                    ).unwrap(),
                ]);
                const modelRevision = selectSceneRevision(modelRevisionsData, {
                    sceneVersionId: procoreContext.sceneVersionId,
                    sceneId,
                });
                return modelRevision
                    ? modelsData.data.find((model) => model.id === modelRevision.bim_model_id)?.name
                    : undefined;
            } catch {
                // Not worth reporting: the scene keeps the name its record holds, or its id.
                return undefined;
            }
        },
        [store, getModelRevisions, getModels],
    );
}
